// src/ui/Home.jsx — what Allo is, for someone who has never heard of it.
//
// 🟥 EVERY CLAIM ON THIS PAGE CAME OUT OF THE CODEBASE. The feature index in
//    featureIndex.js was built by reading the app's 1,550 English strings and
//    the files behind them; the numbers quoted here (12 themes, 19 wallpapers,
//    10 app icons, 240p/480p/720p, 10 languages) were counted from the arrays
//    that define them. Nothing here is aspirational. See the header of
//    featureIndex.js for the list of things this page deliberately does NOT
//    claim, and why.

import React, { useState } from 'react';
import { SiteHeader, SiteFooter } from './SiteChrome';
import { FEATURE_GROUPS, FEATURE_COUNT } from './featureIndex';
import { APP, HOME, navigate } from '../services/siteRoute';
import shotWebChats from '../assets/images/laptop-tablet-chatlist.webp';
import shotWebLogin from '../assets/images/laptop-tablet-login.webp';
import shotWebVibes from '../assets/images/laptop-tablet-vibe.webp';
import shotChats from '../assets/images/phone-chatlist.webp';
import shotThemes from '../assets/images/phone-themes.webp';
import shotVibes from '../assets/images/phone-vibes.webp';
import shotInfo from '../assets/images/phone-chatinfo.webp';
import './home.css';

/* Counted, not estimated — each of these is a number I could point at a file for. */
const STATS = [
  ['10', 'interface languages'],
  ['12', 'chat themes'],
  ['19', 'wallpapers'],
  ['1,550', 'translated strings'],
];

function Shot({ src, alt, kind, priority = false }) {
  /* 🟢 width/height are on the element, not just in CSS. Without them the
     browser cannot reserve the space before the image decodes, so every
     section below jumps down as each screenshot arrives — the single most
     common way a page like this feels broken on a slow connection. */
  const dims = kind === 'phone' ? { width: 600, height: 1260 } : { width: 1400, height: 940 };
  return (
    <img
      className={`hm-shot ${kind}`}
      src={src}
      alt={alt}
      {...dims}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      /* the hero image is what the page is judged on; everything else can wait */
      fetchpriority={priority ? 'high' : 'low'}
    />
  );
}

function Group({ group, open, onToggle }) {
  const id = `fg-${group.id}`;
  return (
    <section className={`hm-grp ${open ? 'open' : ''}`}>
      <h3>
        <button type="button" aria-expanded={open} aria-controls={id} onClick={onToggle}>
          <span className="hm-grp-t">{group.title}</span>
          <span className="hm-grp-n">{group.items.length}</span>
          <svg className="hm-chev" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
            <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </h3>
      {/* 🟥 The panel is always in the DOM, hidden with `hidden`, never
          conditionally rendered. Find-in-page and a search engine both need the
          text to exist — a collapsed section that does not exist until clicked
          is invisible to both, which defeats the point of an index. */}
      <div className="hm-grp-body" id={id} hidden={!open}>
        {group.blurb && <p className="hm-grp-blurb">{group.blurb}</p>}
        <dl>
          {group.items.map(([name, desc]) => (
            <div className="hm-item" key={name}>
              <dt>{name}</dt>
              <dd>{desc}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

export default function Home() {
  /* The first group starts open so the index reads as a list of things rather
     than a row of closed doors. */
  const [open, setOpen] = useState(() => ({ [FEATURE_GROUPS[0].id]: true }));
  const allOpen = FEATURE_GROUPS.every((g) => open[g.id]);
  const toggleAll = () => setOpen(allOpen
    ? {}
    : Object.fromEntries(FEATURE_GROUPS.map((g) => [g.id, true])));

  const openApp = (e) => { e.preventDefault(); navigate(APP); };

  return (
    <div className="hm">
      <SiteHeader route={HOME} />

      {/* ── hero ─────────────────────────────────────────────────────────── */}
      <section className="hm-hero">
        <div className="hm-wrap hm-hero-in">
          <div className="hm-hero-txt">
            <p className="hm-eyebrow">Private messaging</p>
            {/* 🟢 No manual <br>. The break was hand-placed for a 1440px screen
                and turned into a one-word orphan line at every other width;
                `text-wrap: balance` in the stylesheet evens the lines out at
                whatever width the reader actually has. */}
            <h1>Your conversations, sealed on your device.</h1>
            <p className="hm-lede">
              Allo is a messenger for phone and browser: end-to-end encrypted chats,
              voice and video calls, groups, a video feed, and links that let you talk
              to someone who has never installed it.
            </p>
            <div className="hm-cta-row">
              <a className="hm-btn primary" href="/app" onClick={openApp}>Open Allo Web</a>
              <a className="hm-btn ghost" href="#everything">See everything it does</a>
            </div>
            <p className="hm-note">
              Sign in by scanning a QR code with the app.
            </p>
          </div>
          <div className="hm-hero-img">
            <Shot src={shotWebChats} kind="wide" priority
              alt="Allo for Web open on a laptop and a tablet, showing the chat list beside a conversation" />
          </div>
        </div>
      </section>

      {/* ── counted facts ────────────────────────────────────────────────── */}
      <div className="hm-stats">
        <div className="hm-wrap hm-stats-in">
          {STATS.map(([n, label]) => (
            <div className="hm-stat" key={label}>
              <b>{n}</b>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── the encryption pitch ─────────────────────────────────────────── */}
      <section className="hm-band">
        <div className="hm-wrap">
          <h2>Encrypted in a way that survives owning more than one device</h2>
          <p className="hm-band-lede">
            End-to-end encryption is easy to claim and awkward to actually do once a person
            has a phone, a laptop and a tablet. Allo seals every message separately for every
            device you own, so linking a new one does not mean handing a key to a server.
          </p>
          <div className="hm-cards">
            <article>
              <h3>A code you can check</h3>
              <p>
                Every conversation has a 60-digit security code. Compare it by scanning a QR
                or reading it aloud, and both sides can see that the other verified. The phone
                and the browser compute the same code from the same keys.
              </p>
            </article>
            <article>
              <h3>History that moves without leaking</h3>
              <p>
                Link a new device and your existing phone re-encrypts its message history
                specifically for that device and uploads it. Nothing in between can read it,
                including the server it passes through.
              </p>
            </article>
            <article>
              <h3>Privacy with a cost, on purpose</h3>
              <p>
                Hide your last seen and you stop seeing everyone else’s. Turn off read receipts
                and you lose theirs too. The settings are reciprocal because one-way privacy
                is just surveillance with better manners.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ── feature showcases ────────────────────────────────────────────── */}
      <section className="hm-show">
        <div className="hm-wrap">

          <div className="hm-row">
            <div className="hm-row-txt">
              <p className="hm-eyebrow">Chats</p>
              <h2>Everything a conversation needs, and the parts nobody bothers with</h2>
              <p>
                Replies, reactions, forwarding, starred messages, polls, live location, voice
                notes, documents with a viewer built in, and a media hub that collects every
                photo, file, link, poll and location a chat ever held.
              </p>
              <ul className="hm-ticks">
                <li>Messages written offline wait, and say they are waiting</li>
                <li>Delivery and read ticks that respect the other person’s settings</li>
                <li>When a stranger messages first, Allo shows you what it knows about them</li>
                <li>Search one conversation, or all of them at once</li>
              </ul>
            </div>
            <div className="hm-row-img">
              <Shot src={shotChats} kind="phone" alt="The Allo chat list on iPhone" />
            </div>
          </div>

          <div className="hm-row rev">
            <div className="hm-row-txt">
              <p className="hm-eyebrow">Vibes</p>
              <h2>A video feed living inside the messenger</h2>
              <p>
                Vertical reels, photo and text posts, friends, likes and comments, a
                Creative-Commons music library you can search, and analytics on your own posts.
                Every upload is transcoded to three quality levels so it starts fast on any
                connection, and screened before it publishes.
              </p>
              <ul className="hm-ticks">
                <li>For You and Friends, as two separate feeds</li>
                <li>Mix the video’s own audio against the track you added</li>
                <li>Views, reach and engagement on each post</li>
                <li>Strike thresholds published up front: 10, 20, 30</li>
              </ul>
            </div>
            <div className="hm-row-img">
              <Shot src={shotVibes} kind="phone" alt="The Vibes feed on iPhone" />
            </div>
          </div>

          <div className="hm-row wide-img">
            <div className="hm-row-txt">
              <p className="hm-eyebrow">On the web too</p>
              <h2>The same account, the whole product, in a browser</h2>
              <p>
                Not a cut-down companion. Chats, calls, Vibes, media, polls and profiles all
                run in the browser, and a call can move between your laptop and your phone
                mid-conversation.
              </p>
            </div>
            <div className="hm-row-img">
              <Shot src={shotWebVibes} kind="wide" alt="Vibes running in Allo for Web on a laptop and tablet" />
            </div>
          </div>

          <div className="hm-row rev">
            <div className="hm-row-txt">
              <p className="hm-eyebrow">Make it yours</p>
              <h2>Twelve themes, nineteen wallpapers, ten app icons</h2>
              <p>
                Each chat theme is drawn twice so it is right in both light and dark. Nine
                doodle patterns take any custom colour and re-contrast their icons against it.
                Any single conversation can look different from the rest.
              </p>
              <ul className="hm-ticks">
                <li>23 bubble styles — 15 solid, 8 gradient</li>
                <li>Your own photo as a wallpaper, with an opacity preview</li>
                <li>A live demo conversation shows the choice before you make it</li>
              </ul>
            </div>
            <div className="hm-row-img">
              <Shot src={shotThemes} kind="phone" alt="A themed Allo conversation on iPhone" />
            </div>
          </div>

          <div className="hm-row">
            <div className="hm-row-txt">
              <p className="hm-eyebrow">Per-chat control</p>
              <h2>The settings that matter live where the conversation is</h2>
              <p>
                Disappearing messages, a PIN lock with Face ID, media that stays covered until
                you unlock it, per-chat privacy overrides, translation, wallpapers, and the
                security code — all in one place, for this conversation.
              </p>
              <ul className="hm-ticks">
                <li>Disappearing after 3 hours, 12 hours, 1 day or 15 days</li>
                <li>Turning the timer on is announced, not silent</li>
                <li>Private Media covers photos but leaves the text readable</li>
              </ul>
            </div>
            <div className="hm-row-img">
              <Shot src={shotInfo} kind="phone" alt="An Allo chat information screen on iPhone" />
            </div>
          </div>

        </div>
      </section>

      {/* ── calls ────────────────────────────────────────────────────────── */}
      <section className="hm-band alt">
        <div className="hm-wrap">
          <p className="hm-eyebrow center">Calls</p>
          <h2 className="center">When a call struggles, Allo tells you whose fault it is</h2>
          <p className="hm-band-lede center">
            Most apps freeze the picture and say “Reconnecting…”, which is true of everyone and
            useful to nobody. Allo works out which side of the call is actually failing and says
            so. Forty separate messages exist for this one job.
          </p>
          <div className="hm-cards">
            <article>
              <h3>Before you dial</h3>
              <p>
                A weak connection gets a warning and an offer to make it a voice call instead
                of a video one, rather than a failure thirty seconds in.
              </p>
            </article>
            <article>
              <h3>While you talk</h3>
              <p>
                A dropped connection tries to restart itself. Picture-in-picture keeps the call
                floating while you use the phone. Audio moves between earpiece, speaker and
                Bluetooth.
              </p>
            </article>
            <article>
              <h3>Group calls</h3>
              <p>
                Ring a whole group, join one already running, re-invite whoever missed it, and
                put the video tiles on one half of the screen with the chat on the other.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ── guest links ──────────────────────────────────────────────────── */}
      <section className="hm-guest">
        <div className="hm-wrap hm-guest-in">
          <div>
            <p className="hm-eyebrow">Guest links</p>
            <h2>Talk to someone who will never install anything</h2>
            <p className="hm-lede sm">
              Create a link, send it however you like, and they answer in their browser — with
              media, documents, voice notes, replies, polls they can vote in and a send queue
              that survives losing signal. No account, no download.
            </p>
          </div>
          <ol className="hm-steps">
            <li>
              <b>You choose how long it lives</b>
              <span>One, three, seven or fifteen days. Fifteen is the ceiling.</span>
            </li>
            <li>
              <b>Optionally, a passcode</b>
              <span>Hashed on the server. It is never checked on the guest’s device.</span>
            </li>
            <li>
              <b>One link, one person</b>
              <span>
                The first browser to open it claims it. Forwarding the link cannot hand your
                conversation to a stranger.
              </span>
            </li>
            <li>
              <b>End it whenever you want</b>
              <span>Revoking kills the link and deletes the conversation — but the record of
                who you talked to stays in your list.</span>
            </li>
          </ol>
        </div>
      </section>

      {/* ── the full index ───────────────────────────────────────────────── */}
      <section className="hm-index" id="everything">
        <div className="hm-wrap">
          <div className="hm-index-head">
            <div>
              <h2>Everything, in full</h2>
              <p>
                {FEATURE_COUNT} capabilities across {FEATURE_GROUPS.length} areas. Not a
                roadmap — this is what is built.
              </p>
            </div>
            <button type="button" className="hm-btn ghost sm" onClick={toggleAll}>
              {allOpen ? 'Collapse all' : 'Expand all'}
            </button>
          </div>

          <div className="hm-groups">
            {FEATURE_GROUPS.map((g) => (
              <Group
                key={g.id}
                group={g}
                open={!!open[g.id]}
                onToggle={() => setOpen((o) => ({ ...o, [g.id]: !o[g.id] }))}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── close ────────────────────────────────────────────────────────── */}
      <section className="hm-end">
        <div className="hm-wrap">
          <h2>Open it in your browser</h2>
          <p>Scan a QR code with the app and everything above is on this screen.</p>
          <a className="hm-btn primary lg" href="/app" onClick={openApp}>Open Allo Web</a>
          <div className="hm-end-img">
            <Shot src={shotWebLogin} kind="wide" alt="The Allo for Web sign-in screen on a laptop and tablet" />
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
